import { useState, ReactNode, useEffect } from 'react';
import { Paper, Tabs, Tab } from '@mui/material';
import { useSearchParams } from 'react-router-dom';

type TabList = {
    caption: string;
    component: ReactNode;
    show?: boolean;
    icon?: React.ReactElement;
    key?: string;
};

type SttTabsProps = {
    tabs: TabList[];
    fullWidth?: boolean;
    grow?: boolean;
    initialTab?: number;
    urlParam?: string;
    onTabChange?: (tabIndex: number) => void;
};

/**
 *    El contenido de las pestañas se cargan cuando se muestra la primera vez, una vez cargada ya no se vuelve a cargar.
 */
export function SttTabs({
    tabs,
    fullWidth: _fullWidth,
    grow: _grow,
    initialTab = 0,
    urlParam,
    onTabChange,
}: SttTabsProps) {
    const [searchParams, setSearchParams] = useSearchParams();
    const visibleTabs = tabs?.filter((tab) => tab.show ?? true) || [];

    const getInitialTabIndex = (): number => {
        if (urlParam) {
            const tabKey = searchParams.get(urlParam);
            if (tabKey) {
                const index = visibleTabs.findIndex(
                    (tab) => tab.key === tabKey,
                );
                if (index !== -1) return index;
            }
        }
        return initialTab;
    };

    const [currTabIndex, setCurrTabIndex] = useState(getInitialTabIndex);
    const [loadedTabIndexes, setLoadedTabIndexes] = useState<number[]>([
        getInitialTabIndex(),
    ]);
    // Track if user has manually changed tabs
    const [userNavigated, setUserNavigated] = useState(false);

    // Sync with initialTab prop when it changes (e.g., after data loads)
    // Only do this if the user hasn't manually navigated yet
    useEffect(() => {
        if (!userNavigated && initialTab !== currTabIndex) {
            setCurrTabIndex(initialTab);
            if (!loadedTabIndexes.includes(initialTab)) {
                setLoadedTabIndexes((prev) => [...prev, initialTab]);
            }
        }
    }, [initialTab]);

    useEffect(() => {
        if (urlParam) {
            const tabKey = searchParams.get(urlParam);
            if (tabKey) {
                const index = visibleTabs.findIndex(
                    (tab) => tab.key === tabKey,
                );
                if (index !== -1 && index !== currTabIndex) {
                    setCurrTabIndex(index);
                    if (!loadedTabIndexes.includes(index)) {
                        setLoadedTabIndexes([...loadedTabIndexes, index]);
                    }
                }
            }
        }
    }, [searchParams, urlParam]);

    const handleSelectTab = (tabIndex: number) => {
        setUserNavigated(true);
        setCurrTabIndex(tabIndex);

        if (!loadedTabIndexes.includes(tabIndex)) {
            setLoadedTabIndexes([...loadedTabIndexes, tabIndex]);
        }

        if (urlParam && visibleTabs[tabIndex]?.key) {
            setSearchParams({ [urlParam]: visibleTabs[tabIndex].key });
        }

        onTabChange?.(tabIndex);
    };

    return (
        <>
            <Tabs
                value={currTabIndex}
                textColor="primary"
                onChange={(_ev, val: string) => handleSelectTab(Number(val))}
                variant="scrollable"
                scrollButtons="auto"
            >
                {visibleTabs.map((tab, i) => (
                    <Tab
                        key={tab.key || tab.caption}
                        id={`tab${i}`}
                        icon={tab.icon}
                        label={tab.caption}
                    />
                ))}
            </Tabs>

            {visibleTabs.map((tab, idx) =>
                loadedTabIndexes.includes(idx) ? (
                    <Paper
                        key={tab.key || tab.caption}
                        sx={{
                            display: currTabIndex !== idx ? 'none' : 'block',
                            p: 3,
                        }}
                    >
                        {tab.component}
                    </Paper>
                ) : null,
            )}
        </>
    );
}
